// Workflow document and Sage integration types

/**
 * WorkflowDocument represents a supporting document (photo, PDF, lab report)
 * uploaded to a workflow. Documents are stored in S3.
 */
export interface WorkflowDocument {
  id: number;
  batchWorkflowId: number;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  description?: string;
  documentCategory: string;
  uploadedByUserId: number;
  uploadedByUserName?: string;
  createdAt: string;
  downloadUrl?: string;
}

/**
 * DocumentCategory represents the purpose-based category of a workflow document.
 * Values match the backend DocumentCategory enum member names.
 *
 * Note: legacy values ('Client' | 'Agent' | 'Lab') were remapped to 'Other' by
 * migration and are no longer selectable, but the stored `documentCategory` string
 * field on WorkflowDocument is intentionally a free `string` to tolerate any historical value.
 */
export type DocumentCategory =
  | 'NutritionProgram'
  | 'ProductInformation'
  | 'ExternalData'
  | 'FieldPhoto'
  | 'Recommendation'
  | 'Report'
  | 'Other';

/**
 * Selectable document categories with display labels. The `value` matches the
 * backend DocumentCategory enum member name; the `label` is shown in the UI.
 */
export const DOCUMENT_CATEGORY_OPTIONS: { value: DocumentCategory; label: string }[] = [
  { value: 'NutritionProgram', label: 'Nutrition Program' },
  { value: 'ProductInformation', label: 'Product Information' },
  { value: 'ExternalData', label: 'External Data' },
  { value: 'FieldPhoto', label: 'Field Photo' },
  { value: 'Recommendation', label: 'Recommendation' },
  { value: 'Report', label: 'Report' },
  { value: 'Other', label: 'Other' },
];

const DOCUMENT_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  DOCUMENT_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

/**
 * Display label for a stored category value. Falls back to the raw value so legacy
 * rows (e.g. 'Client', 'Agent', 'Lab') still render rather than showing blank.
 */
export function documentCategoryLabel(value: string): string {
  return DOCUMENT_CATEGORY_LABELS[value] ?? value;
}

/**
 * One workflow's documents within a farmer-wide related-documents view.
 * Mirrors the backend RelatedWorkflowDocumentGroupDto.
 */
export interface WorkflowDocumentGroup {
  workflowId: number;
  workflowLabel: string;
  batchId?: number;
  farmName?: string;
  plotName?: string;
  date?: string;
  isCurrentWorkflow: boolean;
  documents: WorkflowDocument[];
}

/**
 * Every document associated with a workflow's farmer, grouped by workflow.
 * Surfaced on the internal review Workspace. Mirrors RelatedWorkflowDocumentsDto.
 */
export interface RelatedWorkflowDocuments {
  farmerId: number;
  farmerName: string;
  truncated: boolean;
  groups: WorkflowDocumentGroup[];
}

export type SageDocumentType = 'Invoice' | 'CreditNote' | 'Quote' | 'PurchaseOrder';
export type SageDocumentStatus = 'Pending' | 'Synced' | 'Failed' | 'Voided' | 'Paid';

/**
 * SageDocumentLink represents a link between a batch workflow and a Sage
 * accounting document (invoice, credit note, quote, purchase order).
 */
export interface SageDocumentLink {
  id: number;
  batchWorkflowId: number;
  documentType: SageDocumentType;
  sageDocumentId: string;
  documentNumber?: string;
  documentDate?: string;
  amount?: number;
  currency?: string;
  status: SageDocumentStatus;
  createdAt: string;
  syncedAt?: string;
  lastSyncError?: string;
}

/**
 * CreateSageDocumentLinkDto is used to link a Sage document to a workflow.
 */
export interface CreateSageDocumentLinkDto {
  documentType: SageDocumentType;
  sageDocumentId: string;
  documentNumber?: string;
  documentDate?: string;
  amount?: number;
  currency?: string;
}

/**
 * UpdateSageDocumentLinkStatusDto is used to update the sync status of a Sage document link.
 */
export interface UpdateSageDocumentLinkStatusDto {
  status: SageDocumentStatus;
  syncError?: string;
}
